import React, { useRef, useState } from "react";
import DataTable from "@/components/DataTable";
import { useTranslation } from "@/langs/useTranslation";
import { getDevicesControlLogTableAPI } from "@/apis/getDeviceControlLogDetailAPI";
import { scheduledDateInLocal } from "./datetimeHandlers";
import { ReactRender } from "@/components/ReactRender";
import TaskStatusBar from "@/components/commons/TaskStatusBar";
import { Chip, IconButton, makeStyles, Tooltip } from "@material-ui/core";
import clsx from "clsx";
import { Refresh } from "@material-ui/icons";

const useStyles = makeStyles(theme => ({
  head: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  stats: {
    marginBottom: theme.spacing(2)
  },
  chip: {
    position: "relative",
    padding: "15px 25px",
    height: "auto",
    borderRadius: 10,
    fontSize: 16,
    marginRight: theme.spacing(2),

    "&:before": {
      content: '""',
      position: "absolute",
      left: 0,
      top: 0,
      bottom: 0,
      right: 0,
      backgroundColor: "currentColor",
      opacity: 0.1
    }
  },
  success: {
    color: "#00B42A",
    borderColor: "currentColor"
  },
  info: {
    color: "#3491FA",
    borderColor: "currentColor"
  }
}));

export const DeviceControlLogDetailDatatable = ({
  groupId,
  className,
  style,
  footer,
  children
}) => {
  const { t } = useTranslation("deviceControl");
  const tableRef = useRef(null);
  const [stats, setStats] = useState({
    failed_count: 0, //失败数量
    completed_count: 0, //成功数量
    processing_count: 0 //执行中数量
  });
  const classes = useStyles();

  const columns = [
    {
      data: "device.serial_number",
      name: "serial_number",
      title: t({ defaultMessage: "设备SN" })
    },
    {
      data: "device.device_name",
      name: "name",
      title: t({ defaultMessage: "设备名称" }),
      sortable: false
    },
    {
      data: "device",
      name: "",
      title: t({ defaultMessage: "门店" }),
      className: "group",
      orderable: true,
      searchable: true,
      sortable: false,
      createdCell: (td, data) => {
        const groups = data?.groups?.map((item, index) => {
          return (
            <span className="owner_name" key={index}>
              {item.name}
            </span>
          );
        });
        ReactRender(groups?.length > 0 ? groups : "-", td);
      }
    },
    {
      data: "device",
      title: t({ defaultMessage: "标签" }),
      sortable: false,
      render: data => {
        if (!data) return "-";
        return data.tags?.map(item => item.name).join(", ");
      }
    },
    {
      data: "device.detail",
      name: "detail",
      sortable: false,
      title: t({ defaultMessage: "描述" })
    },
    {
      data: "created_at",
      title: t({ defaultMessage: "命令时间" }),
      render: data => {
        return scheduledDateInLocal(new Date(data));
      }
    },
    {
      data: "status",
      title: t({ defaultMessage: "结果" }),
      createdCell: (td, data, row) => {
        ReactRender(
          <TaskStatusBar
            status={data}
            isOffline={row.device?.online_offline_status !== "online"}
          />,
          td
        );
      }
    },
    {
      data: "response",
      title: t({ defaultMessage: "讯息" }),
      sortable: false,
      orderable: false,
      createdCell: (td, data) => {
        ReactRender(
          <>{data === "" ? <i>{t("table.noMessage")}</i> : data}</>,
          td
        );
      }
    }
  ];

  const ajax = (data, callback) => {
    return getDevicesControlLogTableAPI({
      ...data,
      task_group_id: groupId
    }).then(res => {
      callback({
        data: res.data,
        draw: res.draw,
        recordsFiltered: res.recordsFiltered,
        recordsTotal: res.recordsTotal
      });
      setStats({
        failed_count: res.failed_count, //失败数量
        completed_count: res.completed_count, //成功数量
        processing_count: res.processing_count //执行中数量
      });
    });
  };

  const handleRefresh = () => {
    tableRef.current?.reload();
  };

  if (!groupId) {
    return (
      <div>
        {t({defaultMessage: "没有设置变更的记录，设备皆采用预设设置"})}
      </div>
    )
  }

  return (
    <>
      <div className={classes.head}>
        <div className={classes.stats}>
          <Chip
            color="primary"
            label={t(
              { defaultMessage: "完成：{num}" },
              { num: stats.completed_count }
            )}
            variant="outlined"
            className={clsx(classes.chip, classes.success)}
          />
          <Chip
            color="secondary"
            label={t(
              { defaultMessage: "失败：{num}" },
              { num: stats.failed_count }
            )}
            variant="outlined"
            className={classes.chip}
          />
          <Chip
            color="primary"
            label={t(
              { defaultMessage: "进行中：{num}" },
              { num: stats.processing_count }
            )}
            variant="outlined"
            className={clsx(classes.chip, classes.info)}
          />
        </div>
        <Tooltip title={t({ defaultMessage: "刷新" })} placement="top">
          <IconButton onClick={handleRefresh}>
            <Refresh />
          </IconButton>
        </Tooltip>
      </div>
      <DataTable
        ref={tableRef}
        ordering
        columns={columns}
        ajax={ajax}
        className={className}
        style={style}
        footer={footer}
        showCheckAll={false}
        autoLoad
      >
        {children}
      </DataTable>
    </>
  );
};
